import Header from "./Header/Header";
import Footer from "./Footer/Footer";
import { useEffect, useMemo, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import Background from "../ui/components/Background";
import {
  DESKTOP_SHELL_HEIGHT,
  MOBILE_SHELL_HEIGHT,
  SAFE_AREA_TOP,
  mobileNavMediaQuery,
} from "./layoutMetrics";
import { SidebarProvider } from "./SidebarContext";
import GenerationNotificationBubble from "./GenerationNotificationBubble";

export default function MainLayout({ children }) {
  const isMobileNav = useMediaQuery(mobileNavMediaQuery);
  const [isSidebarOpen, setSidebarOpen] = useState(true);

  const sidebarContextValue = useMemo(
    () => ({
      isSidebarOpen,
      setSidebarOpen,
      openSidebar: () => setSidebarOpen(true),
      closeSidebar: () => setSidebarOpen(false),
    }),
    [isSidebarOpen],
  );

  // Mobile only: sync shell height to visualViewport so keyboard / browser
  // chrome do not bury the chat composer. Laptop keeps plain 100vh.
  useEffect(() => {
    if (!isMobileNav || typeof window === "undefined" || !window.visualViewport) {
      document.documentElement.style.removeProperty("--app-vvh");
      return undefined;
    }

    const vv = window.visualViewport;
    const sync = () => {
      document.documentElement.style.setProperty("--app-vvh", `${vv.height}px`);
    };

    sync();
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);

    return () => {
      vv.removeEventListener("resize", sync);
      vv.removeEventListener("scroll", sync);
      document.documentElement.style.removeProperty("--app-vvh");
    };
  }, [isMobileNav]);

  return (
    <SidebarProvider value={sidebarContextValue}>
      <Box
        sx={{
          height: isMobileNav ? MOBILE_SHELL_HEIGHT : DESKTOP_SHELL_HEIGHT,
          maxHeight: isMobileNav ? MOBILE_SHELL_HEIGHT : DESKTOP_SHELL_HEIGHT,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          color: "white",
          overflow: isMobileNav ? "hidden" : "visible",
        }}
      >
        <Background />

        <Box sx={{ position: "absolute", top: 0, left: 0, zIndex: 2 }}>
          <Header />
        </Box>

        <Box
          sx={{
            flex: 1,
            position: "relative",
            zIndex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: isMobileNav ? "hidden" : "visible",
            boxSizing: "border-box",
            // Room for fixed mobile menu control; desktop keeps compact top inset.
            pt: isMobileNav
              ? `max(48px, calc(12px + ${SAFE_AREA_TOP}))`
              : `max(20px, calc(8px + ${SAFE_AREA_TOP}))`,
          }}
        >
          {children}
        </Box>

        <GenerationNotificationBubble />

        {/* Profile chip: desktop rail only. Mobile uses drawer Footer. */}
        {!isMobileNav && (
          <Box sx={{ position: "absolute", bottom: 0, left: 0, zIndex: 2 }}>
            <Footer />
          </Box>
        )}
      </Box>
    </SidebarProvider>
  );
}
